<template>
  <DashboardCard :title="$t('Workforce')">
    <WdsTabs v-model:active-tab-key="selectedTab" fitted secondary>
      <WdsStack justify-space-between class="tw-pb-1">
        <WdsMenu placement="bottom-end" manual-trigger z-index="50">
          <template #trigger="{ toggle }">
            <WdsButtonGhost @click="toggle">
              <WdsStack justify-center align-center gap-sm>
                <span>{{ showMonthRange ? $t('Next 30 days') : $t('Next 7 days') }}</span>
                <span><WdsIcon :size="16" name="chevron-down"></WdsIcon></span>
              </WdsStack>
            </WdsButtonGhost>
          </template>
          <WdsMenuOption :active="!showMonthRange" @click="toggleDateRange(7)">
            {{ $t('7 days') }}
            <WdsIcon v-if="!showMonthRange" name="checkmark" :size="16" color="#5d36f1" />
          </WdsMenuOption>
          <WdsMenuOption :active="showMonthRange" @click="toggleDateRange(30)">
            {{ $t('30 days') }}
            <WdsIcon v-if="showMonthRange" name="checkmark" :size="16" color="#5d36f1" />
          </WdsMenuOption>
        </WdsMenu>
        <WdsButtonGhost :href="seeAllHref" secondary class="tw-items-center">
          {{ $t('See all') }}
          <WdsIcon :size="16" name="chevron-right" />
        </WdsButtonGhost>
      </WdsStack>
      <WdsTab :title="$t('Starting soon')" tab-key="starting">
        <template #title>
          <WdsStack align-center gap-sm>
            {{ $t('Starting soon') }}
            <WdsLabel v-if="startingCount > 0" xs class="tw-py-3">
              <span class="tw-px-[2px]">{{ startingCount }}</span>
            </WdsLabel>
          </WdsStack>
        </template>

        <div v-if="loading" class="loading-list">
          <LoadingList :count="2" />
        </div>

        <WdsStack
          v-else-if="startingHires.length === 0"
          align-center
          justify-center
          class="tw-pt-5 tw-pb-8"
        >
          <WdsText secondary>{{ $t('No upcoming hires found') }}</WdsText>
        </WdsStack>

        <WdsStack
          v-else
          gap-sm
          direction-column
          class="worker-list"
          :class="{ 'tw-relative': startingCount > 3 }"
        >
          <div
            v-for="(data, index) in startingHires"
            :key="data.id"
            :data-test="`workforce-starting-hire-${index}`"
          >
            <WdsStack justify-space-between class="worker-container">
              <a
                class="worker tw-w-full tw-h-full"
                :href="
                  data.hasPendingApproval
                    ? laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'approvals' })
                    : laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'overview' })
                "
              >
                <WdsStack align-center gap-sm>
                  <WdsAvatar :path="data.worker.avatarPath ?? ''" />
                  <WdsStack direction-row justify-space-between class="tw-w-full">
                    <WdsStack direction-column>
                      <WdsStack direction-row gap-sm>
                        <WdsTag subtle sm>
                          {{ getHireTimeframeText(data.startDate, true) }}
                        </WdsTag>
                        <WdsTag
                          v-if="data.hasPendingApproval"
                          subtle
                          warning
                          sm
                          data-test="needs-approval-tag"
                        >
                          {{ $t('Needs approval') }}
                        </WdsTag>
                      </WdsStack>
                      <WdsText class="tw-font-normal">{{ data.worker.name }}</WdsText>
                      <WdsText secondary class="tw-overflow-hidden tw-text-ellipsis">
                        {{ data.job.name }}
                      </WdsText>
                    </WdsStack>

                    <WdsStack justify-space-between>
                      <WdsStack align-center>
                        <div v-if="data.activeStatus === HireActiveStatus.Draft">
                          <WdsLabel sm class="worker-status">
                            {{ $t('Draft') }}
                          </WdsLabel>
                        </div>
                        <div
                          v-else-if="data.activeStatus === HireActiveStatus.Offered"
                          class="tw-flex tw-items-center"
                        >
                          <WdsTooltip left class="tw-justify-center">
                            <template #tooltip-content>
                              {{
                                $t(
                                  "The hire is pending classification or awaiting the worker's signature",
                                )
                              }}
                            </template>
                            <WdsLabel subtle primary sm class="worker-status">Offered</WdsLabel>
                          </WdsTooltip>
                        </div>
                        <div v-else-if="data.activeStatus === HireActiveStatus.Ready">
                          <WdsLabel subtle success sm class="worker-status">
                            {{ $t('Ready') }}
                          </WdsLabel>
                        </div>
                        <div v-else-if="data.activeStatus === HireActiveStatus.Active">
                          <WdsLabel success sm class="worker-status">
                            {{ $t('Active') }}
                          </WdsLabel>
                        </div>
                      </WdsStack>
                    </WdsStack>
                  </WdsStack>
                </WdsStack>
              </a>
              <WdsStack align-center justify-center class="tw-ps-4">
                <WdsMenu placement="bottom-end" manual-trigger z-index="50">
                  <template #trigger="{ toggle }">
                    <WdsButtonGhost
                      :data-test="`workforce-starting-menu-${index}`"
                      @click="toggle"
                    >
                      <WdsIcon name="overflow-menu-horizontal" :size="16" />
                    </WdsButtonGhost>
                  </template>

                  <WdsMenuOption
                    v-if="
                      (data.activeStatus === HireActiveStatus.Draft ||
                        data.activeStatus === HireActiveStatus.Ready ||
                        data.activeStatus === HireActiveStatus.Active) &&
                      data.hasPendingApproval
                    "
                    data-test="menu-view-approvals"
                    :href="
                      laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'approvals' })
                    "
                  >
                    <WdsIcon :size="16" name="stamp"></WdsIcon>
                    {{ $t('View approvals') }}
                  </WdsMenuOption>

                  <WdsMenuOption
                    v-if="data.activeStatus === HireActiveStatus.Draft"
                    data-test="menu-manage-hire"
                    :href="laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'overview' })"
                  >
                    <WdsIcon :size="16" name="edit"></WdsIcon>
                    {{ $t('Manage hire') }}
                  </WdsMenuOption>
                  <WdsMenuOption
                    v-else
                    data-test="menu-manage-hire"
                    :href="
                      laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'overview' }) +
                      '?manage-contract=1'
                    "
                  >
                    <WdsIcon :size="16" name="edit"></WdsIcon>
                    {{ $t('Manage hire') }}
                  </WdsMenuOption>

                  <WdsMenuOption
                    v-if="data.activeStatus !== HireActiveStatus.Draft && data.conversation?.id"
                    data-test="menu-message-worker"
                    :href="laravelNamedRoute('messages.show', { id: data.conversation.id })"
                  >
                    <WdsIcon :size="16" name="chat"></WdsIcon>
                    {{ $t('Message worker') }}
                  </WdsMenuOption>

                  <WdsMenuOption
                    v-if="data.activeStatus !== HireActiveStatus.Draft"
                    data-test="menu-download-contract"
                    :href="laravelNamedRoute('yjob.contract', { hire: data.id })"
                  >
                    <WdsIcon :size="16" name="document-download"></WdsIcon>
                    {{ $t('Download contract PDF') }}
                  </WdsMenuOption>

                  <div
                    v-if="
                      data.activeStatus === HireActiveStatus.Draft ||
                      data.activeStatus === HireActiveStatus.Ready ||
                      data.activeStatus === HireActiveStatus.Offered
                    "
                  >
                    <WdsMenuDivider />
                    <WdsMenuOption
                      data-test="menu-cancel-hire"
                      :href="
                        laravelNamedRoute('hires.hire.view', {
                          hire: data.id,
                          tab: 'overview',
                          'cancel-hire': 1,
                        })
                      "
                      theme="DESTRUCTIVE"
                    >
                      <WdsIcon :size="16" name="close-filled"></WdsIcon>
                      {{ $t('Cancel hire') }}
                    </WdsMenuOption>
                  </div>
                </WdsMenu>
              </WdsStack>
            </WdsStack>
          </div>
        </WdsStack>
      </WdsTab>

      <WdsTab :title="$t('Ending soon')" tab-key="ending">
        <template #title>
          <WdsStack align-center gap-sm>
            {{ $t('Ending soon') }}
            <WdsLabel v-if="endingCount > 0" xs>
              <span class="tw-px-[2px]">{{ endingCount }}</span>
            </WdsLabel>
          </WdsStack>
        </template>
        <WdsStack
          gap-sm
          direction-column
          class="worker-list"
          :class="{ 'tw-relative': endingCount > 3 }"
        >
          <div v-if="loading">
            <LoadingList :count="2" />
          </div>

          <WdsStack
            v-else-if="endingHires.length === 0"
            align-center
            justify-center
            class="tw-pt-5 tw-pb-8"
          >
            <WdsText secondary>{{ $t('No ending contracts found') }}</WdsText>
          </WdsStack>

          <div v-else>
            <div
              v-for="(data, index) in endingHires"
              :key="data.id"
              :data-test="`workforce-ending-hire-${index}`"
            >
              <WdsStack justify-space-between class="worker-container">
                <a
                  class="worker tw-w-full tw-h-full"
                  :href="
                    data.hasPendingApproval
                      ? laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'approvals' })
                      : laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'overview' })
                  "
                  :class="{
                    'tw-pointer-events-none tw-cursor-default': !data.conversation?.id,
                  }"
                >
                  <WdsStack align-center gap-sm>
                    <WdsAvatar :path="data.worker.avatarPath ?? ''" />
                    <WdsStack direction-column>
                      <WdsStack align-center>
                        <div>
                          <WdsTag subtle sm>
                            {{ getHireTimeframeText(data.endDate, false) }}
                          </WdsTag>
                        </div>
                      </WdsStack>
                      <WdsText :sub-text="data.job.name">
                        <span class="tw-font-normal">{{ data.worker.name }}</span>
                      </WdsText>
                    </WdsStack>
                  </WdsStack>
                </a>
                <WdsStack align-center justify-center class="tw-ps-4">
                  <WdsMenu placement="bottom-end" manual-trigger z-index="50">
                    <template #trigger="{ toggle }">
                      <WdsButtonGhost
                        :data-test="`workforce-ending-menu-${index}`"
                        @click="toggle"
                      >
                        <WdsIcon name="overflow-menu-horizontal" :size="16" />
                      </WdsButtonGhost>
                    </template>

                    <WdsMenuOption
                      data-test="menu-manage-hire"
                      :href="
                        laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'overview' }) +
                        '?manage-contract=1'
                      "
                    >
                      <WdsIcon :size="16" name="edit"></WdsIcon>
                      {{ $t('Manage hire') }}
                    </WdsMenuOption>

                    <WdsMenuOption
                      v-if="data.conversation?.id"
                      data-test="menu-message-worker"
                      :href="laravelNamedRoute('messages.show', { id: data.conversation.id })"
                    >
                      <WdsIcon :size="16" name="chat"></WdsIcon>
                      {{ $t('Message worker') }}
                    </WdsMenuOption>

                    <WdsMenuOption
                      data-test="menu-download-contract"
                      :href="laravelNamedRoute('yjob.contract', { hire: data.id })"
                    >
                      <WdsIcon :size="16" name="document-download"></WdsIcon>
                      {{ $t('Download contract PDF') }}
                    </WdsMenuOption>

                    <WdsMenuOption
                      data-test="menu-extend-contract"
                      :href="
                        laravelNamedRoute('hires.hire.view', { hire: data.id, tab: 'overview' }) +
                        '?manage-contract=1'
                      "
                    >
                      <WdsIcon :size="16" name="event-schedule"></WdsIcon>
                      {{ $t('Extend contract') }}
                    </WdsMenuOption>
                  </WdsMenu>
                </WdsStack>
              </WdsStack>
            </div>
          </div>
        </WdsStack>
      </WdsTab>
    </WdsTabs>
  </DashboardCard>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsStack,
  WdsText,
  WdsTabs,
  WdsTab,
  WdsAvatar,
  WdsMenu,
  WdsIcon,
  WdsMenuOption,
  WdsLabel,
  WdsMenuDivider,
  WdsTooltip,
  WdsTag,
} from '@worksome/wds-components'
import { ref, computed } from 'vue'
import moment from 'moment'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/gql'
import DashboardCard from '@/components/Dashboard/DashboardCard.vue'
import { useTranslation } from '@/composables/translation'
import { HireActiveStatus } from '@/gql/graphql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'

const { $t, $tc } = useTranslation()
const account = currentAccount.globalId
const showMonthRange = ref(false)
const selectedTab = ref('starting')

const seeAllHref = computed(() => {
  if (selectedTab.value === 'ending') {
    return laravelNamedRoute('frontend.profiles.index.engaged', {
      orderBy: 'eyJmaWVsZCI6IkVORF9EQVRFIiwib3JkZXIiOiJERVNDIn0=',
      activeStatus: 'W3sibGFiZWwiOiJBY3RpdmUiLCJ2YWx1ZSI6IkFDVElWRSJ9XQ==',
    })
  }
  return laravelNamedRoute('frontend.profiles.index.engaged', {
    orderBy: 'eyJmaWVsZCI6IlNUQVJUX0RBVEUiLCJvcmRlciI6IkRFU0MifQ==',
    activeStatus:
      'W3sibGFiZWwiOiJEcmFmdCIsInZhbHVlIjoiRFJBRlQifSx7ImxhYmVsIjoiT2ZmZXJlZCIsInZhbHVlIjoiT0ZGRVJFRCJ9LHsibGFiZWwiOiJBY3RpdmUiLCJ2YWx1ZSI6IkFDVElWRSJ9LHsibGFiZWwiOiJSZWFkeSIsInZhbHVlIjoiUkVBRFkifV0=',
  })
})

const dateRange = computed(() => {
  const today = new Date()
  const rangeEnd = new Date()
  rangeEnd.setDate(today.getDate() + (showMonthRange.value ? 30 : 7))
  return {
    from: today.toISOString().split('T')[0],
    to: rangeEnd.toISOString().split('T')[0],
  }
})

const queryVariables = computed(() => ({
  account: [account],
  startDateRange: dateRange.value,
  endDateRange: dateRange.value,
}))

const { result, loading } = useQuery(
  graphql(`
    query HiresList(
      $account: [ID!]
      $startDateRange: DateRangeInput
      $endDateRange: DateRangeInput
    ) {
      startingHires: hires(accounts: $account, startDateRange: $startDateRange) {
        data {
          id
          startDate
          endDate
          activeStatus
          canCancelContract
          hasPendingApproval
          conversation {
            id
          }
          worker {
            id
            name
            avatar
            avatarPath @client
          }
          job {
            id
            name
          }
        }
      }
      endingHires: hires(accounts: $account, endDateRange: $endDateRange) {
        data {
          id
          startDate
          endDate
          activeStatus
          canCancelContract
          hasPendingApproval
          conversation {
            id
          }
          worker {
            id
            name
            avatar
            avatarPath @client
          }
          job {
            id
            name
          }
        }
      }
    }
  `),
  queryVariables,
  {
    clientId: 'publicApiCustomFields',
  },
)

const startingHires = computed(() => result?.value?.startingHires.data ?? [])
const endingHires = computed(() => result?.value?.endingHires.data ?? [])

const startingCount = computed(() => startingHires.value.length)
const endingCount = computed(() => endingHires.value.length)

const getHireTimeframeText = (date: string, isStarting: boolean): string => {
  const today = moment().startOf('day')
  const targetDate = moment(date).startOf('day')
  const days = targetDate.diff(today, 'days')

  if (days === 0) {
    return isStarting ? $t('Starting today') : $t('Ending today')
  }

  if (days === 1) {
    return isStarting ? $t('Starting tomorrow') : $t('Ending tomorrow')
  }

  const timeText = $tc(':count day | :count days', days)
  return isStarting
    ? $t('Starting in :time', { time: timeText })
    : $t('Ending in :time', { time: timeText })
}

const toggleDateRange = (days: number) => {
  showMonthRange.value = days === 30
}
</script>

<style lang="scss" scoped>
.worker-list {
  @apply tw-mt-1;
  @apply tw-min-h-8;
  @apply tw-max-h-[320px];
  @apply tw-overflow-y-auto;
  @apply tw-pb-2;
}
.worker-container {
  @apply tw-bg-white;
  @apply tw-rounded;
  @apply tw-py-2;
  @apply tw-pl-1;
  @apply tw-pr-5;
  @apply tw-mb-2;
  @apply tw-border-2;
  @apply tw-border-transparent;
  @apply tw-transition;
  @apply tw-duration-200;
}

.worker-container:hover {
  @apply tw-border-2;
  @apply tw-border-gray-200;
}
.worker {
  @apply tw-text-left;
  @apply tw-p-1;
  @apply tw-px-2;
}
.worker-status {
  @apply tw-ml-1;
}
.loading-list {
  @apply tw-pb-4;
}
</style>
